import type { TimedPoint } from "@/lib/replay-core";

export interface AiMockStructure {
  id: string;
  structure_kind: "checkpoint_ring";
  latitude: number;
  longitude: number;
  altitude_m: number;
  size_m: number;
}

export interface AiMockHomePoint {
  latitude: number;
  longitude: number;
  altitude_m: number;
  label: string;
}

export interface AiMockRoute {
  title: string;
  copy: string;
  rationale?: string;
  eta?: string;
  energy?: string;
  noise?: string;
  risk?: string;
  color: string;
  homePoint: AiMockHomePoint;
  structures: AiMockStructure[];
  points: TimedPoint[];
}

function buildTimedRoute(points: [number, number, number][]): TimedPoint[] {
  return points.map(([lat, lon, alt], i) => ({
    latitude: lat,
    longitude: lon,
    altitude_m: alt,
    elapsed_seconds: i * 18,
  }));
}

export type PlannerCriteria = "faster" | "optimal" | "quiet";

export const PLANNER_CONFIGS: Record<PlannerCriteria, AiMockRoute> = {
  faster: {
    title: "Faster route",
    copy: "Shortest mocked travel time with sharper turns and a more aggressive corridor cut.",
    rationale:
      "This mocked plan cuts the middle corridor, accepts tighter lateral margins, and prioritizes arrival time over acoustic discretion.",
    eta: "04:18",
    energy: "71%",
    noise: "High",
    risk: "0.46",
    color: "#FF6B1A",
    homePoint: { latitude: 50.0862, longitude: 14.4308, altitude_m: 235, label: "Mock start" },
    structures: [
      { id: "planner-fast-cp1", structure_kind: "checkpoint_ring", latitude: 50.0872, longitude: 14.4334, altitude_m: 250, size_m: 20 },
      { id: "planner-fast-cp2", structure_kind: "checkpoint_ring", latitude: 50.089, longitude: 14.4376, altitude_m: 270, size_m: 20 },
      { id: "planner-fast-cp3", structure_kind: "checkpoint_ring", latitude: 50.0904, longitude: 14.4414, altitude_m: 255, size_m: 20 },
    ],
    points: buildTimedRoute([
      [50.0862, 14.4308, 235],
      [50.0872, 14.4334, 250],
      [50.089, 14.4376, 270],
      [50.0904, 14.4414, 255],
      [50.0911, 14.4428, 246],
    ]),
  },
  optimal: {
    title: "Most optimal route",
    copy: "Balanced mocked route with cleaner turn geometry and a stronger safety reserve.",
    rationale:
      "This mocked option avoids the largest exclusion circle, preserves a moderate energy budget, and keeps a more stable margin around the corridor edge.",
    eta: "04:54",
    energy: "63%",
    noise: "Medium",
    risk: "0.28",
    color: "#38BDF8",
    homePoint: { latitude: 50.0862, longitude: 14.4308, altitude_m: 235, label: "Mock start" },
    structures: [
      { id: "planner-opt-cp1", structure_kind: "checkpoint_ring", latitude: 50.0871, longitude: 14.433, altitude_m: 248, size_m: 20 },
      { id: "planner-opt-cp2", structure_kind: "checkpoint_ring", latitude: 50.0886, longitude: 14.4363, altitude_m: 265, size_m: 20 },
      { id: "planner-opt-cp3", structure_kind: "checkpoint_ring", latitude: 50.0901, longitude: 14.4408, altitude_m: 252, size_m: 20 },
    ],
    points: buildTimedRoute([
      [50.0862, 14.4308, 235],
      [50.0871, 14.433, 248],
      [50.0886, 14.4363, 265],
      [50.0901, 14.4408, 252],
      [50.0908, 14.4421, 248],
    ]),
  },
  quiet: {
    title: "Most quiet route",
    copy: "Acoustically conservative mocked route with wider arcs and a longer transit time.",
    rationale:
      "This mocked plan gives up directness to stay away from the hotter zones, using broader arcs and a softer climb profile to keep the projected noise envelope lower.",
    eta: "05:38",
    energy: "67%",
    noise: "Low",
    risk: "0.22",
    color: "#A78BFA",
    homePoint: { latitude: 50.0862, longitude: 14.4308, altitude_m: 235, label: "Mock start" },
    structures: [
      { id: "planner-quiet-cp1", structure_kind: "checkpoint_ring", latitude: 50.0867, longitude: 14.4324, altitude_m: 242, size_m: 20 },
      { id: "planner-quiet-cp2", structure_kind: "checkpoint_ring", latitude: 50.0878, longitude: 14.4358, altitude_m: 250, size_m: 20 },
      { id: "planner-quiet-cp3", structure_kind: "checkpoint_ring", latitude: 50.0898, longitude: 14.4404, altitude_m: 244, size_m: 20 },
    ],
    points: buildTimedRoute([
      [50.0862, 14.4308, 235],
      [50.0867, 14.4324, 242],
      [50.0878, 14.4358, 250],
      [50.0898, 14.4404, 244],
      [50.0907, 14.4416, 242],
    ]),
  },
};

export type ScenarioLocation = "coastal" | "urban" | "forest";

export const SCENARIO_CONFIGS: Record<ScenarioLocation, AiMockRoute> = {
  coastal: {
    title: "Coastal range scenario",
    copy: "Mocked coastal route with one blocked corridor, two checkpoints, and a silent extraction finish.",
    color: "#38BDF8",
    homePoint: { latitude: 43.5082, longitude: 16.4403, altitude_m: 22, label: "Mock launch" },
    structures: [
      { id: "scenario-coastal-cp1", structure_kind: "checkpoint_ring", latitude: 43.5094, longitude: 16.4448, altitude_m: 30, size_m: 20 },
      { id: "scenario-coastal-cp2", structure_kind: "checkpoint_ring", latitude: 43.5104, longitude: 16.4493, altitude_m: 26, size_m: 20 },
    ],
    points: buildTimedRoute([
      [43.5082, 16.4403, 22],
      [43.5094, 16.4448, 30],
      [43.5104, 16.4493, 26],
      [43.5109, 16.4511, 28],
    ]),
  },
  urban: {
    title: "Dense urban scenario",
    copy: "Mocked urban route with rooftop insertion, corridor scan, and a shielded handoff exit.",
    color: "#FBBF24",
    homePoint: { latitude: 50.0873, longitude: 14.4236, altitude_m: 230, label: "Mock launch" },
    structures: [
      { id: "scenario-urban-cp1", structure_kind: "checkpoint_ring", latitude: 50.0882, longitude: 14.4263, altitude_m: 255, size_m: 20 },
      { id: "scenario-urban-cp2", structure_kind: "checkpoint_ring", latitude: 50.0891, longitude: 14.4298, altitude_m: 248, size_m: 20 },
    ],
    points: buildTimedRoute([
      [50.0873, 14.4236, 230],
      [50.0882, 14.4263, 255],
      [50.0891, 14.4298, 248],
      [50.0895, 14.4311, 242],
    ]),
  },
  forest: {
    title: "Forest corridor scenario",
    copy: "Mocked forest route with canopy breach, thermal sweep, and a creekside exfil corridor.",
    color: "#4ADE80",
    homePoint: { latitude: 49.7465, longitude: 14.3174, altitude_m: 368, label: "Mock launch" },
    structures: [
      { id: "scenario-forest-cp1", structure_kind: "checkpoint_ring", latitude: 49.7473, longitude: 14.3201, altitude_m: 386, size_m: 20 },
      { id: "scenario-forest-cp2", structure_kind: "checkpoint_ring", latitude: 49.7482, longitude: 14.3234, altitude_m: 378, size_m: 20 },
    ],
    points: buildTimedRoute([
      [49.7465, 14.3174, 368],
      [49.7473, 14.3201, 386],
      [49.7482, 14.3234, 378],
      [49.7486, 14.3245, 372],
    ]),
  },
};
